import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CollectorAction } from '@/domain/collectors/collector'
import { VoucherType, InvoiceData } from '@/domain/invoices/invoice'
import { PaymentTransaction } from '@/domain/payments/payments'
import { useCollectors } from '@/hooks/api/useCollectors'
import { useState } from 'react'

interface ReturnPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  payment: PaymentTransaction | null
  invoice?: InvoiceData | null
  onConfirm: (action: CollectorAction) => void
  isPending: boolean
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount)
}

export const ReturnPaymentModal = ({
  isOpen,
  onClose,
  payment,
  invoice,
  onConfirm,
  isPending,
}: ReturnPaymentModalProps) => {
  const [selectedAction, setSelectedAction] = useState<CollectorAction>(CollectorAction.PAYED_COLLECTOR)
  const { data: collectors } = useCollectors()
  const collector = collectors?.find(c => c.id === payment?.collectorId)

  const handleConfirm = () => {
    onConfirm(selectedAction)
  }

  const getImpactLabel = (): string => {
    if (!payment) return ''
    const amount = formatCurrency(payment.amount)
    if (selectedAction === CollectorAction.PAYED_COLLECTOR) {
      return 'Pago completado, rendido por el cobrador'
    }
    if (invoice?.voucherType === VoucherType.INVOICE) {
      return `Nota de crédito por ${amount}`
    }
    return `Devolución por ${amount}`
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pago en Cobrador {collector?.code} - {collector?.name}</DialogTitle>
          <DialogDescription>
            {selectedAction === CollectorAction.FROM_COLLECTOR
              ? 'Devolver un pago asignado a un cobrador, restableciéndolo al estado original'
              : 'Confirmar que el pago fue entregado al cobrador'}
          </DialogDescription>
        </DialogHeader>

        {payment && (
          <>
            <div className="space-y-3 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Acción</label>
                <Select value={selectedAction} onValueChange={(val) => setSelectedAction(val as CollectorAction)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar acción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CollectorAction.PAYED_COLLECTOR}>Rendir pago</SelectItem>
                    <SelectItem value={CollectorAction.FROM_COLLECTOR}>Devolver pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted/30 p-3 rounded-lg border space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Monto del pago:</span>
                  <span className="text-sm font-semibold">{formatCurrency(payment.amount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Estado actual:</span>
                  <Badge variant="info">En cobrador</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Impacto:</span>
                  <span className="text-sm text-destructive font-semibold">{getImpactLabel()}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <div className='flex justify-between w-full'>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isPending}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirm}
                  disabled={isPending}
                >
                  {isPending ? 'Procesando...' : selectedAction === CollectorAction.FROM_COLLECTOR ? 'Confirmar devolución' : 'Confirmar rendición'}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
